/// <amd-module name="universalTypeahead" />
/// <amd-dependency path="jquery" name="$"/>
/// <amd-dependency path="uitk" name="uitk"/>
/// <amd-dependency path="uitk_i18n" name="i18n"/>
/// <amd-dependency path="uitk_tooltip"/>
/// <amd-dependency path="typeahead/core/loc" name="loc"/>
/// <amd-dependency path="typeahead/core/context" name="coreContext"/>

/// <reference path="../../typings/typeahead/browser.d.ts" />
/// <reference path="../../typings/thirdparty/modernizr.d.ts" />
/// <reference path="../../typings/typeahead/modulizr.d.ts" />
/// <reference path="../../typings/typeahead/window.d.ts" />
/// <reference path="../../typings/thirdparty/conditionizr.d.ts" />
/// <reference path="../../typings/typeahead/uitk.d.ts" />
/// <reference path="../../typings/typeahead/ExpediaSuggestServiceRequestGenerator.d.ts" />
/// <reference path="../../typings/typeahead/universalTypeahead.d.ts" />
/// <reference path="../../typings/typeahead/loc.d.ts" />
/// <reference path="../../typings/typeahead/plugins.d.ts" />

import {ClientHandlerRegistry} from "typeahead/client/ClientHandlerRegistry";
import {ConfigurableFeature} from "typeahead/core/ConfigurableFeature";
import {EssDataProvider, IEssResponse} from "typeahead/core/EssDataProvider";
import {ExpediaSuggestServiceRequestGenerator} from "typeahead/core/ExpediaSuggestServiceRequestGenerator";
import {
    GoogleMapsDataProvider,
    ICombinedGoogleEssResponse,
    IGoogleMapsDataProviderOptions,
} from "typeahead/core/google/GoogleMapsDataProvider";
import {IDataProviderResponse} from "typeahead/core/IDataProvider";
import {ISuggesterResponse} from "typeahead/core/ISuggester";
import {OmnitureLogger} from "typeahead/core/logging/OmnitureLogger";
import {PerformanceMetricsLogger} from "typeahead/core/logging/PerformanceMetricsLogger";
import {ProblemOrigin} from "typeahead/core/logging/ProblemOrigin";
import {ScanabilityLogger} from "typeahead/core/logging/ScanabilityLogger";
import {SelectionToSubmissionLogger} from "typeahead/core/logging/SelectionToSubmissionLogger";
import {Severity} from "typeahead/core/logging/Severity";
import {SuggestionEngagementLogger} from "typeahead/core/logging/SuggestionEngagementLogger";
import TypeaheadErrorLogger from "typeahead/core/logging/TypeaheadErrorLogger";
import {TypeaheadInteractionLogger} from "typeahead/core/logging/TypeaheadInteractionLogger";
import {ElementFocusedMessage} from "typeahead/core/message/ElementFocusedMessage";
import {EssErrorMessage} from "typeahead/core/message/EssErrorMessage";
import {InputClearedMessage} from "typeahead/core/message/InputClearedMessage";
import {InputValueEditedMessage} from "typeahead/core/message/InputValueEditedMessage";
import {MoreSuggestionsClickedMessage} from "typeahead/core/message/MoreSuggestionsClickedMessage";
import {SuggestionListSelectedMessage} from "typeahead/core/message/SuggestionListSelectedMessage";
import {SuggestionReadyMessage} from "typeahead/core/message/SuggestionReadyMessage";
import {SuggestionSelectedMessage} from "typeahead/core/message/SuggestionSelectedMessage";
import {SuggestionShownMessage} from "typeahead/core/message/SuggestionShownMessage";
import {TypeaheadMessage} from "typeahead/core/message/TypeaheadMessage";
import {UserExitedMessage} from "typeahead/core/message/UserExitedMessage";
import {OnFocusSuggester} from "typeahead/core/OnFocusSuggester";
import {CompositePageLoadHandler} from "typeahead/core/plugins/CompositePageLoadHandler";
import {DefaultPluginAssembly} from "typeahead/core/plugins/DefaultPluginAssembly";
import {SuggestionsProvider} from "typeahead/core/plugins/SuggestionsProvider";
import {DisplayGroupHandler, ISortedDisplayGroupItem} from "typeahead/core/presentation/DisplayGroupHandler";
import {DropdownContentHandlersAssembly} from "typeahead/core/presentation/DropdownContentHandlersAssembly";
import {DropDownHideHandlersAssembly} from "typeahead/core/presentation/DropDownHideHandlersAssembly";
import {DropDownShowHandlersAssembly} from "typeahead/core/presentation/DropDownShowHandlersAssembly";
import {
    IInputElementFocusByHumanOrScriptHandler,
    isInputElementFocusByHumanOrScriptHandler,
} from "typeahead/core/presentation/IInputElementFocusByHumanOrScriptHandler";
import {
    IInputElementFocusHandler,
    isInputElementFocusHandler,
} from "typeahead/core/presentation/IInputElementFocusHandler";
import {ITypeaheadDropdownModel, TypeaheadDropdownBuilder} from "typeahead/core/presentation/TypeaheadDropdownBuilder";
import * as SearchParametersUpdater from "typeahead/core/SearchParametersUpdater";
import {
    ICombinedStaticEssResponse,
    IStaticDataProviderOptions,
    StaticDataProvider,
} from "typeahead/core/StaticDataProvider";
import {IEssSuggestionResult, Suggestion} from "typeahead/core/Suggestion";
import * as SuggestionFactory from "typeahead/core/SuggestionFactory";
import {TypeaheadUtils} from "typeahead/core/TypeaheadUtils";
import {mapMethodByName} from "typeahead/core/util/Functional";
import {
    IClientSideCacheStatusChecker,
    isClientSideCacheDisabled,
} from "typeahead/dataprovider/IClientSideCacheStatusChecker";

class UniversalTypeaheadImpl implements IUniversalTypeahead {

    /*
     this plugin assembly is parked here so that we could wrap it in this module
     the rationale I put it here is so that we don't introduce additional dependency
     in the same time, it could be considered as our plugin registry
     */
    public static pluginAssembly: DefaultPluginAssembly = new DefaultPluginAssembly();

    public static initOmnitureLogger(): void {
        UniversalTypeaheadImpl.oLogger = new OmnitureLogger();
    }

    public static getOmnitureLogger(): OmnitureLogger {
        return UniversalTypeaheadImpl.oLogger;
    }

    // Omniture logger instance
    private static oLogger: OmnitureLogger;

    private static performanceMetricsLogger: PerformanceMetricsLogger;
    private static seLogger: SuggestionEngagementLogger;
    private static interactionLogger: TypeaheadInteractionLogger;
    private static scanabilityLogger: ScanabilityLogger;
    private static selectionToSubmissionLogger: SelectionToSubmissionLogger;

    // jQuery objects
    private $input: any;
    private $parent: JQuery;
    private $label: JQuery;
    private $popupContent: JQuery;

    private opts: any;
    private ariaOwns: string;
    private firstItemFlag: boolean;
    private lastItemFlag: boolean;
    private searchType: any;
    private ignoreNext: boolean;
    private cache: {[query: string]: IDataProviderResponse};
    private isClickOn: boolean;
    // Used to identify if the template requested is "default" or "by category".
    // This is to fix previous issue with Android+Talkback in which
    // the keyboard needed to be triggered twice
    private templateUsed: any;

    private renderedData: IRenderedData;

    private essDataProvider: EssDataProvider = new EssDataProvider();
    private requestGenerator: ExpediaSuggestServiceRequestGenerator;
    private suggestionsProvider: SuggestionsProvider = new SuggestionsProvider(UniversalTypeaheadImpl.pluginAssembly);
    private context: IContext;

    private googleMapsDataProvider: GoogleMapsDataProvider = new GoogleMapsDataProvider();
    private staticDataProvider: StaticDataProvider = new StaticDataProvider();

    private defaultEssOptions: any = {
        abtest: "",
        additionalDataSourceUrl: "",
        dest: false,
        features: [],
        lob: TypeaheadUtils.LOB_ATTRIBUTE.FLIGHTS,
        locale: "en_US",
        mask: TypeaheadUtils.defaultMask,
        maxitems: 20,
        maxresults: 10,
        overrideDataRequest: null, // if present, allows client to take over the Ajax request completely
        overrideServiceUrl: "",
        serviceUrl: "//suggest.expedia.com",
        version: "v2",
    };

    private defaultTAOptions: any = {
        // Some options may be overwriten with data-XXX html attributes on the input element

        closetext: loc.closetext,
        continuetext: loc.continuetext,
        forceicon: false,
        minchar: 1,
        /**
         * notifications
         * The gcTypeahead control will notify when these events happen.
         * They don't expect
         */
        onInputBlur: null,
        onInputFocus: null,
        /**
         * called when an item is selected with mouse or keyboard, with one argument that is the selected <a>.
         */
        onItemSelected: null,
        onItemSelectionChanged: null,
        onPopupClosed: null,
        onPopupOpened: null,
        openDefaultTa: false,
        playBackText: "",
        /**
         * transform hooks
         * preRenderTransform is a hook to allow updating or transforming
         * the list of items to be rendered.
         * Caution: called inline when rendering. Should be synchronous and not
         * take too long to avoid UI junk.
         * Takes in a search term and a list of items
         * Returns a new list of items
         * Used within render thusly: results
         */
        preRenderTransform: null,
        selectFirstElementByDefault: true,
        tooltip: true,
    };

    private defaultOptions: any = $.extend({}, this.defaultTAOptions, this.defaultEssOptions);

    private displayGroupHandler: DisplayGroupHandler = new DisplayGroupHandler();

    private dropdownContentHandlersAssembly: DropdownContentHandlersAssembly =
        new DropdownContentHandlersAssembly(UniversalTypeaheadImpl.pluginAssembly);

    private dropDownShowHandlersAssembly: DropDownShowHandlersAssembly =
        new DropDownShowHandlersAssembly(UniversalTypeaheadImpl.pluginAssembly);

    private dropDownHideHandlersAssembly: DropDownHideHandlersAssembly =
        new DropDownHideHandlersAssembly(UniversalTypeaheadImpl.pluginAssembly);

    private readonly inputElementFocusByHumanOrScriptHandlers: IInputElementFocusByHumanOrScriptHandler [] =
        UniversalTypeaheadImpl.pluginAssembly.get(isInputElementFocusByHumanOrScriptHandler);

    private readonly inputElementFocusHandlers: Array<IInputElementFocusHandler<any>> =
        UniversalTypeaheadImpl.pluginAssembly.get(isInputElementFocusHandler);

    private onFocusSuggester: OnFocusSuggester = new OnFocusSuggester(UniversalTypeaheadImpl.pluginAssembly.get(
        DefaultPluginAssembly.onFocusSuggestionsProviderIdentifier));

    private clientSideCacheStatusChecker: IClientSideCacheStatusChecker[] =
        UniversalTypeaheadImpl.pluginAssembly.get(isClientSideCacheDisabled);

    private hasFocus: boolean;

    private readonly ESS_EXCEPTION_RESPONSE_CODES: string[] = [TypeaheadUtils.ZERO_RESULTS_CAUSED_BY_EXCEPTION];

    private oldInputVal: string;

    constructor(inputId: string, template: any, options: any) {
        // The <input>
        this.$input = $(inputId);

        // The <input>'s parent (wrapping div)
        this.$parent = this.$input.closest("." + TypeaheadUtils.responsiveTAClasses.autoCompleteClass);

        // The <input>'s <label>
        this.$label = this.$input.closest("label");

        // Defaults positioning of the autocomplete dropdown to the left. If another class is set, it will stay as is
        this.setPinClass(this.$parent);

        // clone the options - https://api.jquery.com/jquery.extend/#jQuery-extend-deep-target-object1-objectN
        this.opts = $.extend(true, {}, this.defaultOptions, options, {
            browser: TypeaheadUtils.getBrowser(),
            device: TypeaheadUtils.getDeviceType(),
            guid: TypeaheadUtils.getGuid(),
            locale: coreContext.locale,
            serviceUrl: TypeaheadUtils.getServiceUrl(),
            siteid: coreContext.siteid,
        });

        this.requestGenerator = this.constructRequestGenerator(this.opts, UniversalTypeaheadImpl.pluginAssembly);
        if (!this.opts.clientId) {
            this.opts.clientId = TypeaheadUtils.getClientId();
        }

        this.opts.packageType = TypeaheadUtils.getShortPackageType(this.$input);

        this.context = this.constructContext(this.opts);
        this.opts.featureOverrides = ConfigurableFeature.getFeatureOverrides(this.$input);

        this.ariaOwns = this.$input.attr("aria-owns") || "backwards";

        this.templateUsed = template;

        // Accessibility
        this.firstItemFlag = true;
        this.lastItemFlag = true;

        // State:
        this.searchType = null;
        this.cache = {};
        this.ignoreNext = false; // ???
        this.isClickOn = false;

        // Set up the input and listeners:
        this.$input.autocomplete = "off";

        this.$input
            .on("focus", $.proxy(this.focus, this))
            .on("keydown", $.proxy(this.keyDown, this))
            .on("keyup", $.proxy(this.keyUp, this))
            .on("keypress", $.proxy(this.keyPress, this))
            .on("input", $.proxy(this.input, this))
            .on("blur", $.proxy(this.blur, this));

        // gcTypeahead popup dialog UI is rendered in this element
        this.$popupContent = this.$input
            .closest("." + TypeaheadUtils.responsiveTAClasses.autoCompleteClass)
            .children("." + TypeaheadUtils.responsiveTAClasses.dropdownClass);

        // attach a reference of this instance to the input
        // acts as a marker to signal that a gcTypeahead control
        // is attached to the to the element.
        this.$input.data("typeahead", this);

        // Sometimes the first dataRequest is too slow, we ping the service ahead of
        // time to help speed it up:
        if (this.opts.openDefaultTa) {
            this.dataRequest("defaultResponse");
        }

        this.renderedData = {
            query: "",
            results: [],
        };

        if (!UniversalTypeaheadImpl.seLogger) {
            UniversalTypeaheadImpl.seLogger =  new SuggestionEngagementLogger();
        }

        if (!UniversalTypeaheadImpl.interactionLogger) {
            UniversalTypeaheadImpl.interactionLogger = new TypeaheadInteractionLogger();
        }

        if (!UniversalTypeaheadImpl.scanabilityLogger) {
            UniversalTypeaheadImpl.scanabilityLogger = new ScanabilityLogger();
        }

        if (!UniversalTypeaheadImpl.selectionToSubmissionLogger) {
            UniversalTypeaheadImpl.selectionToSubmissionLogger = new SelectionToSubmissionLogger();
        }

        if (!UniversalTypeaheadImpl.oLogger) {
            UniversalTypeaheadImpl.oLogger = new OmnitureLogger();
        }

        if (!UniversalTypeaheadImpl.performanceMetricsLogger) {
            UniversalTypeaheadImpl.performanceMetricsLogger = new PerformanceMetricsLogger();
        }

        TypeaheadMessage.subscribe(
            InputClearedMessage.getTopic(), (topic: string, message: InputClearedMessage) => {
                if (message && message.$elem) {
                    const clearedElementId: string = message.$elem.attr("id");
                    if (this.$input.attr("id") === clearedElementId) {
                        this.input(null);
                    }
                }
            },
        );

        // Listener to close typeahead when clicking anywhere outside of typeahead
        $(document.body).bind("mousedown", (event) => {
            if (!$(event.target).is(this.$input)
                && $(event.target).parents("#userFeedbackToolModal, #appreciationModal").length < 1) {
                this.close();
            }
        });
    }

// private helper functions
    private getCategory(result: any): any {
        // Uses "feature detection" rather than looking for
        // a specific version or whatever.
        // returns a type or undefined
        if (result.category === "USERHISTORY") {
            return TypeaheadUtils.CATEGORIES.USERHISTORY;
        } else if (typeof result.type !== "undefined") {
            const overrideIcon = TypeaheadUtils.getOverrideIcon(result.type, result.subtype);
            if (typeof overrideIcon === "string") {
                return TypeaheadUtils.iconOverride(TypeaheadUtils.CATEGORIES[result.type], overrideIcon);
            }
            return TypeaheadUtils.CATEGORIES[result.type];
        } else if (typeof result.t !== "undefined") {
            return TypeaheadUtils.CATEGORIES[result.t];
        }
    }

    private addIndexToItems(items: any): void {
        items.forEach((item, i) => {
            item.index = i;
        });
    }

    private updateSearchSuggestions(suggestions: any[], suggestionsProvider: SuggestionsProvider, context: IContext,
                                    forceIcon: boolean, essAbTests: string): void {

        function setSuggestionIcon(suggestion: any, category: any) {
            if (forceIcon) {
                suggestion.icon = forceIcon;
            } else {
                suggestion.icon = category ? category.icon : "location";
            }
        }

        function updateSuggestionLastSearchName(suggestion: any) {
            if (typeof suggestion.regionNames !== "undefined") {
                if (typeof suggestion.regionNames.lastSearchName !== "undefined") {
                    suggestion.regionNames.lastSearchName =
                        suggestion.regionNames.lastSearchName.replace(/'/g, "&#39;");
                } else if (typeof suggestion.regionNames.fullName !== "undefined") {
                    suggestion.regionNames.fullName = suggestion.regionNames.fullName.replace(/'/g, "&#39;");
                    suggestion.regionNames.lastSearchName = suggestion.regionNames.fullName;
                }
            }
        }

        /**
         * categoryVM is a specific model attribute for the category template.
         * It is not returned by ESS, it is added to differentiate it with the other model attributes.
         */
        function updateSuggestionForCategoryTemplate(suggestion: any) {
            suggestion.categoryVM = { displayName: "", fullName: "" };
            if ("regionNames" in suggestion) {
                suggestion.categoryVM.displayName = suggestion.regionNames.displayName;
                suggestion.categoryVM.fullName = suggestion.regionNames.fullName;
                suggestion.categoryVM.lastSearchName = suggestion.regionNames.lastSearchName;
            } else if ("d" in suggestion) {
                suggestion.categoryVM.displayName = suggestion.d;
                suggestion.categoryVM.fullName = suggestion.l;
            }
        }

        const that = this;
        // TODO : Typesafety of this.opts and refactor what options are being passed to other functions
        // modify Suggestions
        const modifiedSuggestions =
            this.suggestionsProvider.modifySuggestions(suggestions, context, this.opts, essAbTests);
        $.each(modifiedSuggestions, (i: number, suggestion: any): void => {
            const category = that.getCategory(suggestion);
            suggestion.categoryName = category ? category.name : "Other";
            if (!suggestion.icon) {
                setSuggestionIcon(suggestion, category);
            }
            updateSuggestionLastSearchName(suggestion);
            updateSuggestionForCategoryTemplate(suggestion);
        });
    }

    private constructContext(opts: any): IContext {
        return {
            clientId: opts.clientId,
            deviceType: TypeaheadUtils.getDeviceType(),
            expuserid: TypeaheadUtils.getExpUserId(),
            getShortPackageType: (): string => TypeaheadUtils.getShortPackageType(),
            guid: opts.guid,
            lob: opts.lob,
            locale: opts.locale,
            serviceUrl: opts.serviceUrl,
            siteid: (opts.siteid).toString(10),
            tab: ClientHandlerRegistry.getTab(),
        } as IContext;
    }

    private constructSuggestionServiceUrlOptions(opts: any): ISuggestServiceUrlOptions {
        return {
            locale: opts.locale,
            overrideServiceUrl: opts.overrideServiceUrl,
            serviceUrl: opts.serviceUrl,
            suggestServiceUrl: opts.suggestServiceUrl,
        } as ISuggestServiceUrlOptions;
    }

    private constructRequestGenerator(opts: any,
                                      pluginAssembly: IPluginAssembly): ExpediaSuggestServiceRequestGenerator {
        return new ExpediaSuggestServiceRequestGenerator(
            ExpediaSuggestServiceRequestGenerator.ESS_ABACUS_IDS,
            typeof ExpediaSuggestServiceRequestGenerator.defaultVariantAssigner === "function" ?
                ExpediaSuggestServiceRequestGenerator.defaultVariantAssigner : undefined,
            this.constructSuggestionServiceUrlOptions(opts),
            opts.siteid,
            pluginAssembly,
        );
    }

    private getDomElementId(): string {
        return this.$input.attr("id");
    }

    private showPopup(searchQuery: string, activeEssTests: string): void {
        if (this.$popupContent.is(":hidden")) {
            // handle mouse messages
            this.$popupContent
                .off("mousedown")
                .off("mouseup")
                .off("mouseenter")
                .off("mouseleave")
                .on("mousedown", $.proxy(this.clickOn, this))
                .on("mouseup", $.proxy(this.clickOff, this))
                .on("mouseenter", "a", $.proxy(this.mouseEnter, this))
                .on("mouseleave", "a", $.proxy(this.mouseLeave, this));

            this.$popupContent.closest("." + TypeaheadUtils.responsiveTAClasses.autoCompleteClass).
                addClass(TypeaheadUtils.responsiveTAClasses.openClass);
            this.$popupContent.show();
            this.dropDownShowHandlersAssembly.runHandlers(this.$popupContent, this.$input, this.context);
        }
        this.dropdownContentHandlersAssembly.runHandlers(this.$popupContent, this.$input, this.context, searchQuery,
            activeEssTests);
    }

    private startRequest(): void {
        const minQueryLength: number = (typeof this.opts.minchar === "number") ? this.opts.minchar : 1;
        const srchText = this.$input.val();
        if (srchText.length >= minQueryLength) {
            const cachedEssResponse: IEssResponse = this.cache[srchText] as IEssResponse;
            const renderCachedEssResponse = () => {
                this.render(cachedEssResponse.query, cachedEssResponse.requestId, cachedEssResponse.results,
                    cachedEssResponse.activeABTests, cachedEssResponse.overriddenABTests,
                    cachedEssResponse.metadata, cachedEssResponse.responseCode);
            };
            if (!cachedEssResponse) {
                this.dataRequest(srchText);
            } else {
                // Temporary plugin to test effect of removing uTA client side cache
                if (this.clientSideCacheStatusChecker && this.clientSideCacheStatusChecker.length &&
                        this.clientSideCacheStatusChecker[0].isClientSideCacheDisabled(this.context)) {
                    this.dataRequest(srchText);
                } else {
                    renderCachedEssResponse();
                }
            }
        } else if (srchText === "") {
            this.$input.triggerHandler("focus", true);
        } else {
            this.close();
        }
    }

    private createTypeaheadOptions(srchText): any {
        return $.extend(true, {}, this.opts, {
            context: this.context,
            inputElementId: this.getDomElementId(),
            requestGenerator: this.requestGenerator,
            srchText,
        });
    }

    private dataRequest(srchText: string): void {
        this.essDataProvider
            .fetchData(this.createTypeaheadOptions(srchText))
            .then($.proxy(this.essResponseResolver, this))
            .fail((error) => {
                TypeaheadErrorLogger.log(error, Severity.MustFix, ProblemOrigin.ExpediaSuggestService);
                TypeaheadMessage.publish(new EssErrorMessage(error.status));
            });
    }

    private essResponseResolver(essResponse: IEssResponse): void {
        if (essResponse) {
            // Make a call to static dataProvider for all essResponses
            const staticDataOptions: IStaticDataProviderOptions = $.extend(true, {}, this.opts, {
                activeEssTests: essResponse.activeABTests,
                inputElem: this.$input,
                overriddenEssTests: essResponse.overriddenABTests,
                requestType: essResponse.responseCode,
                srchText: essResponse.query,
            });

            this.staticDataProvider.fetchData(staticDataOptions, essResponse)
                .then(( mergedStaticEssResponse: ICombinedStaticEssResponse) => {
                    if (mergedStaticEssResponse.isReferralToGoogleMaps) {
                        const options: IGoogleMapsDataProviderOptions = $.extend(true, {}, this.opts, {
                            activeEssTests: mergedStaticEssResponse.activeABTests,
                            domElementId: this.getDomElementId(),
                            metadata: mergedStaticEssResponse.metadata,
                            overriddenEssTests: mergedStaticEssResponse.overriddenABTests,
                            requestId: mergedStaticEssResponse.requestId,
                            requestType: mergedStaticEssResponse.responseCode,
                            srchText: mergedStaticEssResponse.query,
                        });

                        this.googleMapsDataProvider.fetchData(options, mergedStaticEssResponse)
                            .then((mergedResponse: ICombinedGoogleEssResponse) => {
                                if (mergedResponse) {
                                    this.renderSuggestionsForResponse(mergedResponse);
                                } else {
                                    this.close();
                                }
                            });
                    } else {
                        this.cache[mergedStaticEssResponse.query] = mergedStaticEssResponse;
                        if (mergedStaticEssResponse.results) {
                            this.render(mergedStaticEssResponse.query,
                                mergedStaticEssResponse.requestId,
                                mergedStaticEssResponse.results,
                                mergedStaticEssResponse.activeABTests,
                                mergedStaticEssResponse.overriddenABTests,
                                mergedStaticEssResponse.metadata,
                                mergedStaticEssResponse.responseCode,
                            );
                        } else {
                            this.close();
                        }
                    }
                });
        }
    }

    private renderSuggestionsForResponse(response: ICombinedGoogleEssResponse): void {
        this.cache[response.query] = response;
        if (response.results && response.results.length > 0) {
            this.render(response.query, response.requestId, response.results, response.activeABTests,
                response.overriddenABTests, response.metadata, response.responseCode);
        } else {
            this.close();
        }
    }

    private appendUserTypedText(results: any[], text: string): void {
        let addPlayBackText: boolean = true;
        const textResult: any = {};
        const regex = /\d/;
        const regexScript = /<script[\s\S]+?script\s*>?/ig;
        let str;

        if (this.opts.playBackText && text !== "defaultResponse") {

            $.each(results, (i, result) => {
                if (result.id === -1) {
                    addPlayBackText = false;
                }
            });

            if (addPlayBackText) {
                // scrub input for script tags
                text = text.replace(regexScript, "");

                // What's happening here is that the "playBackText" string that's passed
                // in via the options parameter is expected to have a digit that will be
                // replaced by the search term text.
                // Example:
                //      "Hello, '0'".replace(/\d/, 'world');
                // will output the string
                //     Hello, 'world'
                // This is a poor man's way of doing a simple format string.
                str = this.opts.playBackText;
                str = str.replace(regex, text);

                textResult.l = text;
                textResult.d = str;
                textResult.id = -1;
                textResult.i = -1;
                if (this.opts.lob === "HOTELS") {
                    textResult.t = "HOTEL";
                }

                // append the new play back text line item
                results.push(textResult);
            }
        }
    }

    private render(srchTerm: string, requestId: string, suggestions: any[], activeABTests: string,
                   overriddenABTests: string, metadata: string, essResponseCode?: string): void {
        if ( srchTerm === "") {
            $.extend( this.opts, {renderTrigger : "focus"} );
        } else {
            $.extend( this.opts, {renderTrigger : "type"} );
        }
        const renderStart: number = Date.now();

        /* if input element text value does not match with the search term value, it means that the response received
             is for a previous input text value and should not be rendered */
        if (srchTerm && this.$input.val() !== srchTerm && !this.opts.openDefaultTa) {
            this.publishSuggestionReadyMessage(srchTerm, suggestions, activeABTests, this.getDomElementId(),
                essResponseCode, metadata);
            return;
        }
        let results: any[] = $.extend(true, [], suggestions);  // (jQuery) clone the suggestions array
        // if there are no results available, do not render and close the dropdown if already open
        if (results.length === 0) {
            this.close();
            this.publishSuggestionReadyMessage(srchTerm, suggestions, activeABTests, this.getDomElementId(),
                essResponseCode, metadata);
            return;
        }
        let resultsByDisplayGroup: ISortedDisplayGroupItem[];
        let resultsMessage;
        let resultsNo;
        let viewModel: ITypeaheadDropdownModel;

        if ($(document.activeElement).attr("id") !== this.$input.attr("id")) {
            // if this input control is not the one with focus, then don't render
            return;
        }

        this.appendUserTypedText(results, srchTerm);

        this.addIndexToItems(results);

        // if we have a preRenderTransform hook registered, call it now
        // This hook allows outside callers to alter, add to, or even replace the list
        // of search results. The call is expected to return the new list, however it's been altered.
        // NOTE: We may not want this hook as it allows outside caller to totally alter the
        // the list of items that's been carefully crafted to work with the templates. Perhaps a
        // hook like this should only allow altering say the display name or something????
        if (this.opts.preRenderTransform && typeof this.opts.preRenderTransform === "function") {
            results = this.opts.preRenderTransform(srchTerm, results);
            if (!results || !Array.isArray(results)) {
                throw new Error("preRenderTransform callback must return a valid array.");
            }
        }
        const essAbTests: string = activeABTests ? activeABTests : "";

        this.updateSearchSuggestions(results, this.suggestionsProvider, this.context, this.opts.forceicon, essAbTests);

        // re-index items after allowing updates to list
        this.addIndexToItems(results);

        resultsByDisplayGroup = this.displayGroupHandler.getResultsByDisplayGroup(results);

        viewModel = {
            closeText: this.opts.closetext,
            resultsByDisplayGroup,
        };

        // Fix for Android with Talkback on
        // if (!(("android" in Modernizr) && Modernizr.android)) {
        //     viewModel.ariaOwns = this.ariaOwns;
        // }

        viewModel.ariaOwns = this.ariaOwns;

        this.renderedData.query = srchTerm;
        this.renderedData.results = $.extend(true, [], results);

        this.$popupContent.html(TypeaheadDropdownBuilder.getHtml(viewModel));
        this.$popupContent.attr("data-request_id",  requestId);
        this.showPopup(srchTerm, essAbTests);

        this.$label.addClass("focused");

        resultsNo = results.length;
        if (resultsNo === 1) {
            resultsMessage = "typeahead_results_displayed_one";
        } else {
            resultsMessage = "typeahead_results_displayed_other";
        }

        if (conditionizr.safari) {
            setTimeout(() => {
                uitk.utils.liveAnnounce(i18n.t(resultsMessage, {
                    arg0: resultsNo,
                }), "polite");
                setTimeout(() => {
                    uitk.utils.liveAnnounce("", "polite");
                }, 200); // Reset the last spoken text
            }, 800); // Delay to give enough time for the voice over to finish saying the current word
        } else {
            uitk.utils.liveAnnounce(i18n.t(resultsMessage, {
                arg0: resultsNo,
            }), "polite");
            setTimeout(() => {
                uitk.utils.liveAnnounce("", "polite");
            }, 200); // Reset the last spoken text - for JAWS support
        }

        const renderEnd: number = Date.now();
        const renderTime: number = renderEnd - renderStart;
        TypeaheadMessage.publish(new SuggestionShownMessage(this.renderedData.results, srchTerm, activeABTests,
            this.opts.lob, renderTime, this.getDomElementId()));
        this.publishSuggestionReadyMessage(srchTerm, suggestions, activeABTests, this.getDomElementId(),
            essResponseCode, metadata);
        window.utaTestHooks.latestEssResults = this.renderedData.results;

    }

    private close(): void {
        // unhook mouse messages
        this.$popupContent
            .off("mousedown", $.proxy(this.clickOn, this))
            .off("mouseup", $.proxy(this.clickOff, this))
            .off("mouseenter", "a", $.proxy(this.mouseEnter, this))
            .off("mouseleave", "a", $.proxy(this.mouseLeave, this));

        if (this.$popupContent.is(":hidden")) {
            return;
        }
        setTimeout(() => {
            this.$label.removeClass("focused");
            this.$popupContent.closest("." + TypeaheadUtils.responsiveTAClasses.autoCompleteClass).removeClass(
                TypeaheadUtils.responsiveTAClasses.openClass);

            this.removeActiveDescendant();

            this.$popupContent.unbind("mousedown", $.proxy(this.cancelEvent, this));
            $(document.body).unbind("mousedown", $.proxy(this.close, this));

            this.dropDownHideHandlersAssembly.runHandlers(this.$popupContent, this.$input, this.context);
            const scrollTopToRestore = $(document.body).scrollTop();

            $(document.body).scrollTop(scrollTopToRestore);
            this.$popupContent.hide(); // This might be redundant
            this.$popupContent.html("");
        }, 0);
    }

    // Accessibility
    // Added to remove the aria-activedescendant attribute when closing the dropdown. This was previously
    // taken care automatically by Tooltips. The attribute should be removed once the dropdown is closed for
    // any reason including selection, blur, etc.
    private removeActiveDescendant(): void {
        const needToRemove = $("input[aria-activedescendant]");

        if (needToRemove.length > 0) {
            needToRemove.removeAttr("aria-activedescendant");
        }
    }

    // this is to filter out child suggestions which are hidden in hotels lob when the first result is a region,
    // until they are expanded.
    private getVisibleSuggestionLinks(): JQuery {
        return this.$popupContent.find("a").filter(function() {
            return $(this).parent().css("display") !== "none";
        });
    }

    private prev(): void {
        const links = this.getVisibleSuggestionLinks();
        const current = this.$popupContent.find("." +
            TypeaheadUtils.responsiveTAClasses.hlClass).removeClass(TypeaheadUtils.responsiveTAClasses.hlClass);
        const i = links.index(current);
        const prevIndex = i - 1;
        let prev = links[prevIndex];
        let $prev = $(prev);
        const prevID = $(prev).attr("id");
        const lastItem = links.length - 1;
        let accyText = "";

        if (prevIndex < -1) {
            // when prevIndex is -2 (or less)
            // the user has up-arrowed back into the input box.
            // On the next up-arrow, roll around to the last item or bottom
            // in the list, whatever that is (could be "Close" button if we have one)
            prev = links[lastItem];
            $prev = $(prev);
        }

        if (prev) {

            $(prev).addClass(TypeaheadUtils.responsiveTAClasses.hlClass);
            // if we have a on onInputFocus handler registered, call it now
            if (this.opts.onItemSelectionChanged && typeof this.opts.onItemSelectionChanged === "function") {
                this.opts.onItemSelectionChanged($prev);
            }
            if ($(prev).hasClass("close")) {
                uitk.utils.liveAnnounce((loc.closeSuggestions || i18n.t("close")), "assertive");
            } else if ($(prev).hasClass("feedbackYes")) {
                uitk.utils.liveAnnounce(loc.areSuggestionsHelpfulMessage + " " + loc.agreeMessage, "polite");
            } else if ($(prev).hasClass("feedbackNo")) {
                uitk.utils.liveAnnounce(loc.areSuggestionsHelpfulMessage + " " + loc.disagreeMessage, "polite");
            } else if ($(prev).hasClass("sorryButton")) {
                uitk.utils.liveAnnounce(loc.helpImproveTypeaheadMessage, "polite");
            } else {
                accyText = $(prev).attr("data-value");
                // conditionally doing liveAnnounce as it is causing NVDA to read extraneous data-value on Firefox
                if (!conditionizr.firefox) {
                    uitk.utils.liveAnnounce(accyText, "polite");
                }

                this.$input.attr("aria-activedescendant", prevID);
            }
        } else {
            // "reset" by removing attributes
            this.$input.removeAttr("aria-activedescendant");
            // Accessibility: when user navigates back to input, read the value (Toby)
            uitk.utils.liveAnnounce(this.$input.val(), "polite");
        }
    }

    private next(): void {
        const links = this.getVisibleSuggestionLinks();
        const current = this.$popupContent.find("." +
            TypeaheadUtils.responsiveTAClasses.hlClass).removeClass(TypeaheadUtils.responsiveTAClasses.hlClass);
        const i = links.index(current);
        const nextIndex = i + 1;
        const next = links[nextIndex];
        const $next = $(next);
        const nextID = $(next).attr("id");

        if (next) {
            $next.addClass(TypeaheadUtils.responsiveTAClasses.hlClass);
            // if we have a on onInputFocus handler registered, call it now
            if (this.opts.onItemSelectionChanged && typeof this.opts.onItemSelectionChanged === "function") {
                this.opts.onItemSelectionChanged($next);
            }
            if ($(next).hasClass("close")) {
                uitk.utils.liveAnnounce((loc.closeSuggestions || i18n.t("close")), "assertive");
            } else if ($(next).hasClass("feedbackYes")) {
                uitk.utils.liveAnnounce(loc.areSuggestionsHelpfulMessage + " " + loc.agreeMessage);
            } else if ($(next).hasClass("feedbackNo")) {
                uitk.utils.liveAnnounce(loc.areSuggestionsHelpfulMessage + " " + loc.disagreeMessage);
            } else if ($(next).hasClass("sorryButton")) {
                uitk.utils.liveAnnounce(loc.helpImproveTypeaheadMessage, "polite");
            } else {
                this.$input.attr("aria-activedescendant", nextID);
                // conditionally doing liveAnnounce as it is causing NVDA to read extraneous data-value on Firefox
                if (!conditionizr.firefox) {
                    uitk.utils.liveAnnounce($(next).attr("data-value"), "polite");
                }
            }
        } else {
            // Accessibility: navigated to input, read the value (Toby)
            uitk.utils.liveAnnounce(this.$input.val(), "polite");
        }
    }

    private removeHighlights(): void {
        this.$popupContent.find("." +
            TypeaheadUtils.responsiveTAClasses.hlClass).removeClass(TypeaheadUtils.responsiveTAClasses.hlClass);
    }

    private getSelectedSuggestion(itemId: string): Suggestion {
        if (this.renderedData.results && itemId) {
            const resultIndex: string = itemId.substring(itemId.lastIndexOf("-") + 1);
            // Check if the result has Child
            const nextIndex: number = parseInt(resultIndex, 10) + 1;
            let hasChild: boolean = false;
            if (nextIndex < this.renderedData.results.length) {
                const nextSuggestion: IEssSuggestionResult = this.renderedData.results[nextIndex];
                hasChild = (nextSuggestion.hierarchyInfo && nextSuggestion.hierarchyInfo.isChild === "true");
            }
            return SuggestionFactory.createSuggestion(this.renderedData.results[resultIndex], hasChild);
        }
        return null;
    }

    private selectHighlighted(e: BaseJQueryEventObject, closeFlag: boolean): void {
        let $item = this.$popupContent.find("." + TypeaheadUtils.responsiveTAClasses.hlClass);
        const itemTextFull = $item.text();
        let itemText: string;
        let closeText;
        const resultsClass = "results-item";
        const target = $(e.target);

        if (target.hasClass("chevron-icon")) {
            TypeaheadMessage.publish(new MoreSuggestionsClickedMessage(e, this.renderedData.results, this.opts.lob));
            return;
        }

        // either the closeFlag was specifically passed in or the
        // the user actually clicked (or pressed enter key) on the close button
        closeFlag = closeFlag || target.hasClass("close") || target.parent().hasClass("close") ||
            $item.hasClass("close");

        if ($item.hasClass("feedbackYes") || $item.hasClass("feedbackNo") || $item.hasClass("sorryButton")) {
            $item.trigger("click");
            return;
        }

        // used for touch devices as they don't have hover and therefore don't highlight
        if (uitk.isTouchDevice && !itemTextFull) {
            $item = target.closest("." + resultsClass).find("a");
        }
        let selectedSuggestion: Suggestion;
        if ($item && $item.attr("id")) {
            selectedSuggestion = this.getSelectedSuggestion($item.attr("id"));
            if (selectedSuggestion.getType().orElse("") === "LIST") {
                TypeaheadMessage.publish(
                    new SuggestionListSelectedMessage(selectedSuggestion, this.opts.lob, this.getDomElementId()));
                return;
            }
        }
        // this trims whitespace off ends of the string
        itemText = itemTextFull.replace(/(^\s+|\s+$)/g, "");

        this.updateTextField($item);
        $item.removeClass(TypeaheadUtils.responsiveTAClasses.hlClass);

        // Accessibility
        // Announces that a selection has been made and the value selected,
        // or that the user closed the menu with the close button.
        // NOTE: once the sentences have been localized, we can simplify this logic below.

        if (closeFlag === true) {
            // the close button was selected, so
            // get the potentially localized 'close' text
            closeText = $item.text().replace(/(^\s+|\s+$)/g, "");
            uitk.utils.liveAnnounce(i18n.t("close_button_menu_closed"));
            this.close();
        } else {
            const msg = i18n.t("item_selected_menu_closed", {
                arg0: itemText,
            });
            uitk.utils.liveAnnounce(msg, "assertive");
        }
        // Publish selected suggestion payload
        if (selectedSuggestion) {
            SearchParametersUpdater.updateForSuggestion(
                this.$input.val(), selectedSuggestion, this.opts, this.getDomElementId());
            const requestId: string = this.$popupContent.attr("data-request_id") || "";
            TypeaheadMessage.publish(new SuggestionSelectedMessage(selectedSuggestion, requestId, e, this.opts.lob,
                this.getDomElementId()));
        }

        $item.context = this.$input;
        // Publish select element event on typeahead select (Used by HSRP client)
        // NOTE : expose the html item and the typeaheadData for HSR client
        const data = {
            element: $item,
            suggestionSelected: selectedSuggestion,
        };
        if (selectedSuggestion) {
            uitk.publish("typeahead.resultSelected", data);
        }
        // Added to support CSRP / UITK typeahead callback
        if ("function" === typeof this.opts.selectioncallback) {
            this.opts.selectioncallback($item);
        } else if ("function" === typeof window[this.opts.selectioncallback]) {
            window[this.opts.selectioncallback]($item);
        }
    }

    private updateTextField($item: JQuery): void {
        let lastDashIndex: number;
        let resultIndex: number;
        let searchResult: any;
        let value: any;

        const itemId: string = $item.attr("id");

        if ($item && itemId) {
            lastDashIndex = itemId.lastIndexOf("-");
            resultIndex = +itemId.substring(lastDashIndex + 1);
            searchResult = this.renderedData.results[resultIndex];

            value = $item.attr("data-value");
            if (value) {
                value = value.replace(/&#39;/g, "'");
                this.$input.val(value);

                // Added below condition to fix -  https://jira.sea.corp.expecn.com/jira/browse/ECOMSVC-2758
                if (searchResult.category !== "GEOLOCATION") {
                    /* Added since change event is not implicitly triggered in Safari / IE11 / Edge browsers.
                    For more details, refer - https://jira.sea.corp.expecn.com/jira/browse/ECOMSVC-2749 */
                    this.$input.trigger("change");
                }
                const id = $item.attr("data-id");
                if (id) {
                    this.$input.attr("data-id", id);
                }
            }
            // This is to enable the clear button once the field is updated with text
            uitk.prepFields();
            this.close();
        }
    }

    // Currently only ja_JP has the static typeahead,
    // but trigger onfocus suggestions when there is no static dropdown
    private showOnFocusSuggestions(): void {
        // do not show on focus suggestions on Desktop in ja_JP locale and if full screen typeahead is not enabled
        if (this.context.locale !== "ja_JP" || this.$input.closest("div.col").hasClass("typeahead-overlay") ||
            this.$input.closest("div.col").hasClass("typeahead-non-mobile-overlay-autocomplete-parent")) {
            const oldInputValue: string = this.$input.val();
            this.onFocusSuggester.suggest(this.createTypeaheadOptions(""), this.context)
                .done($.proxy((response: ISuggesterResponse) => {
                    if (response) {
                        // render only if input value is empty or has not changed
                        if (!this.$input.val() || oldInputValue === this.$input.val()) {
                            // Call to render at focus
                            this.render("", response.requestId, response.results,
                                response.activeABTests ? response.activeABTests : "",
                                response.overriddenABTests ? response.overriddenABTests : "",
                                response.metadata);
                        }
                    }
                }, this));
        } else {
            // close open dropdown
            this.close();
        }
    }

    private focus(evt: JQueryEventObject, showSuggestionsOverride: boolean): void {
        const hasInputFocus = (): boolean => {
            return (evt && evt.currentTarget && $(evt.currentTarget).attr("data-hasfocus") === "true");
        };
        if (!hasInputFocus() || showSuggestionsOverride)   {
            mapMethodByName(this.inputElementFocusByHumanOrScriptHandlers, "handleInputElementFocusByHumanOrScript",
                this.$input, this.context);
            // isTrigger is set if event is triggered by script
            if (!evt.isTrigger || showSuggestionsOverride) {
                TypeaheadMessage.publish(
                    new ElementFocusedMessage(this.$input.val(), this.context, this.getDomElementId()));
                mapMethodByName(this.inputElementFocusHandlers, "handleInputElementFocus",
                    this.$input, this.context);
                this.showOnFocusSuggestions();
                // if we have a on onInputFocus handler registered, call it now
                if (this.opts.onInputFocus && typeof this.opts.onInputFocus === "function") {
                    this.opts.onInputFocus(evt);
                }
            }
        }
        // this is to guard against multiple focus events getting fired at the same time in HSRP
        if (evt && evt.currentTarget) {
            $(evt.currentTarget).attr("data-hasfocus", "true");
        }
        // Since package subtypes use the same input instance on Storefront
        this.opts.packageType = TypeaheadUtils.getShortPackageType(this.$input);
    }

    private blur(evt: JQueryEventObject): void {
        // if we have a on onInputBlur handler registered, call it now
        if (this.opts.onInputBlur && typeof this.opts.onInputBlur === "function") {
            this.opts.onInputBlur(evt);
        }

        // IE10 Win8 fix
        if (window.navigator.msPointerEnabled && !window.PointerEvent) {
            return;
        }

        // need a delay so we can cancel if the ta is being clicked on
        if (this.isClickOn) {
            this.$popupContent.bind("mousedown", $.proxy(this.cancelEvent, this));
        }

        if (!$("#userFeedbackToolModal").length && !$("#appreciationModal").length)  {
            if (evt && evt.currentTarget) {
                $(evt.currentTarget).removeAttr("data-hasfocus");
            }
            const requestId = this.$popupContent.attr("data-request_id") || "";
            TypeaheadMessage.publish(new UserExitedMessage(this.context, this.getDomElementId(), evt, requestId));
        }
    }

    private keyDown(evt: JQueryKeyEventObject): void {
        let keyCode;

        if (!evt) {
            evt = window.event as JQueryKeyEventObject;
        }

        keyCode = evt.keyCode;

        switch (keyCode) {
            case 27: // Escape
                this.close();
                evt.stopPropagation();
                break;

            case 9: // Tab
            case 13: // Enter
                if (this.opts.selectFirstElementByDefault) {
                    if (this.$popupContent.find(".highlight").length <= 0 && this.$label.hasClass("focused")) {
                        let liElm = this.$popupContent.find("li.category-item");
                        if (!liElm || liElm.length === 0) {
                            liElm = this.$popupContent.find("li.results-item");
                        }
                        if (liElm) {
                            const firstLiElm = liElm.first();
                            if (firstLiElm) {
                                firstLiElm.find("a").addClass("highlight");
                            }
                        }
                    }
                }
                if (this.$popupContent.find(".highlight").length) {
                    // The stopPropagation() in the following line might be a source of bugs similar to these:
                    //     https://jira/jira/browse/MAIN-155786
                    //     https://jira/jira/browse/MAIN-155786
                    // The target of the event is the <input>, on which there may be other handlers for the same keyDown
                    // event, and stopPropagation() may prevent those handlers from running. That may not be desirable.
                    // The reason why that line was added cannot be discovered, and it is unclear whether removing it
                    // will cause regression.
                    evt.stopPropagation();
                    if (keyCode === 13) {
                        evt.preventDefault();
                    }
                    this.selectHighlighted(evt, this.$popupContent.find(".highlight").hasClass("close"));
                }
                break;
            case 32: // space
                if (this.$popupContent.find(".highlight").hasClass("close")) {
                    evt.stopPropagation();
                    evt.preventDefault();
                }
                break;
            case 37: // Left
            case 39: // Right
                if ((TypeaheadUtils.isRtl() && keyCode === 37) || (!TypeaheadUtils.isRtl() && keyCode === 39)) {
                    if (this.$popupContent.find(".highlight").length) {
                        this.ignoreNext = true;
                        // The stopPropagation() in the following line might be a source of bugs similar to these:
                        //     https://jira/jira/browse/MAIN-155786
                        //     https://jira/jira/browse/MAIN-155786
                        // The target of the event is the <input>, on which there may be other handlers for the same
                        // keyDown event, and stopPropagation() may prevent those handlers from running.
                        // That may not be desirable. The reason why that line was added cannot be discovered,
                        // and it is unclear whether removing it will cause regression.
                        evt.stopPropagation();
                        this.selectHighlighted(evt, false);
                    }
                }
                break;
            case 38: // Up
                evt.preventDefault();
                evt.stopPropagation();
                this.prev();
                break;

            case 40: // Down
                evt.stopPropagation();
                evt.preventDefault();
                this.next();
                break;
            default:
        }
    }

    private keyPress(evt: JQueryKeyEventObject): void {
        if (!evt) {
            evt = window.event as JQueryKeyEventObject;
        }
        if (evt.keyCode === 13 /* Enter/Return */) {
            this.close();
        }
    }

    private keyUp(evt: JQueryKeyEventObject): void {
        // prevents a key up after an item has been selected by the key board
        if (this.ignoreNext) {
            this.ignoreNext = false;
            return;
        }
        if (!evt) {
            evt = window.event as JQueryKeyEventObject;
        }
        switch (evt.keyCode) {
            case 37: // Left
                if (!TypeaheadUtils.isRtl()) {
                    this.removeHighlights();
                }
                break;

            case 39: // Right
                if (TypeaheadUtils.isRtl()) {
                    this.removeHighlights();
                }
                break;

            case 38: // Up
                evt.preventDefault();
                evt.stopPropagation();
                break;

            case 40: // Down
                evt.stopPropagation();
                evt.preventDefault();
                break;

            case 13: // Enter/Return
                break;

            case 32: // space
                if (this.$popupContent.find(".highlight").hasClass("close")) {
                    evt.stopPropagation();
                    evt.preventDefault();
                    this.selectHighlighted(evt, this.$popupContent.find(".highlight").hasClass("close"));
                }
                break;

            default:
                // Moved logic to input listener rather than keyup
        }
    }

    private input(evt: JQueryKeyEventObject): void {
        TypeaheadMessage.publish(new InputValueEditedMessage(this.$input.val(), this.opts.lob, this.getDomElementId()));
        // Guard against IE11 bug that triggers 'input' event on placeholder change
        if (this.$input.val() !== this.oldInputVal) {
            this.oldInputVal = this.$input.val();
            this.startRequest();
            setTimeout(() => {
                SearchParametersUpdater.updateForSuggestion(
                    this.$input.val(), undefined, this.opts, this.getDomElementId());
            });
            // Callback
            if (this.opts.onInputEdited) {
                this.opts.onInputEdited(this.$input.val());
            }
        }
    }

    private clickOn(evt): void {
        const target = $(evt.target);
        if ( ! (target.hasClass("close") || target.parent().hasClass("close")) ) {
            // only set this flag when the user clicks on anything
            // but the close button
            this.isClickOn = true;
        }
        this.click(evt);
    }

    private clickOff(): void {
        this.isClickOn = false;
    }

    private click(evt: JQueryEventObject) {
        evt.stopPropagation();
        evt.preventDefault();
        // A click triggers a selection if and only if it's done with the left mouse button.
        if (evt.button === 0 || uitk.isTouchDevice) {
            this.selectHighlighted(evt, false);
        }
    }

    private mouseEnter(e: BaseJQueryEventObject) {
        const currentElement = e.target;
        if ($(currentElement).is("a.feedbackNo, a.feedbackYes, a.sorryButton, a.close")) {
            return;
        }
        this.removeHighlights();
        $(e.currentTarget).addClass("highlight");
    }

    private mouseLeave(e: BaseJQueryEventObject) {
        this.removeHighlights();
    }

    private cancelEvent(evt: BaseJQueryEventObject) {
        evt.cancelBubble = true;
        if (evt.stopPropagation) {
            evt.stopPropagation();
        }
        if (evt.preventDefault) {
            evt.preventDefault();
        }
        evt.returnValue = false;
    }

    private remove(): void {
        this.$input.autocomplete = "on";
        this.$input.closest("form").attr("autocomplete", "on");

        this.$input
            .off("focus", $.proxy(this.focus, this))
            .off("keydown", $.proxy(this.keyDown, this))
            .off("keyup", $.proxy(this.keyUp, this))
            .off("keypress", $.proxy(this.keyPress, this))
            .off("input", $.proxy(this.input, this))
            .off("blur", $.proxy(this.blur, this));
        this.$popupContent
            .off("mousedown", $.proxy(this.clickOn, this))
            .off("mouseup", $.proxy(this.clickOff, this))
            .off("mouseenter", "a", $.proxy(this.mouseEnter, this))
            .remove(); // removes this element from the DOM as well as all its children
    }

    private setPinClass($parent: JQuery): void {
        if (!$parent.hasClass(TypeaheadUtils.responsiveTAClasses.leftClass) &&
            !$parent.hasClass(TypeaheadUtils.responsiveTAClasses.rightClass) &&
            !$parent.hasClass(TypeaheadUtils.responsiveTAClasses.centerClass)) {
            $parent.addClass(TypeaheadUtils.responsiveTAClasses.leftClass);
        }
    }

    private publishSuggestionReadyMessage(srchTerm, suggestions, activeABTests, domElementId, essResponseCode,
                                          metadata): void {
        if (this.ESS_EXCEPTION_RESPONSE_CODES.indexOf(essResponseCode) < 0) {
            TypeaheadMessage.publish(new SuggestionReadyMessage(srchTerm, suggestions, activeABTests, domElementId,
                metadata));
        } else {
            TypeaheadMessage.publish(new EssErrorMessage(essResponseCode));
        }
    }
}

export function handlePageLoad(): void {
    window.utaTestHooks = {};
    UniversalTypeaheadImpl.initOmnitureLogger();

    // Page load handlers have to be called after all amd modules have initialized
    setTimeout(() => {
        // For mercury wizard (Storefront clients), wait for gc/pageModel to be ready before calling page load handlers
        if (TypeaheadUtils.isStorefrontClient()) {
            require(["gc/pageModel"], (gcPageModel) => {
                CompositePageLoadHandler.getInstance().handlePageLoad(UniversalTypeaheadImpl.pluginAssembly);
            });
        } else {
            CompositePageLoadHandler.getInstance().handlePageLoad(UniversalTypeaheadImpl.pluginAssembly);
        }
    }, 0);
}

export function initTA(input: any, template: string, options: any): IUniversalTypeahead {
    return new UniversalTypeaheadImpl(input, template, options);
}

export function registerPlugin(plugin: any) {
    UniversalTypeaheadImpl.pluginAssembly.registerPlugin(plugin);
}
